import { forwardRef, useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@core/utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  readonly label?: string;
  readonly hint?: string;
  readonly error?: string;
}

/** Shared field chrome for Input / TextArea / Select — one source of truth
 *  so every form control themes, focuses and errors identically. */
export const fieldBase =
  'w-full rounded-lg border border-line bg-bg text-sm text-text transition-colors ' +
  'hover:border-muted disabled:opacity-60 disabled:hover:border-line ' +
  'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:hover:border-danger';

const FIELD = `${fieldBase} px-3 py-2.5 placeholder:text-muted`;

export const labelClass =
  'dda-readout mb-1.5 block text-muted';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, className, ...props },
  ref,
) {
  const { t } = useTranslation();
  const autoId = useId();
  const [revealed, setRevealed] = useState(false);
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  // Every password field gets the reveal, rather than an opt-in prop each new
  // one would have to remember: a generated credential is retyped from another
  // screen, and masked input is where that goes wrong silently.
  const isPassword = props.type === 'password';

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className={labelClass}>
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          className={cn(FIELD, isPassword && 'pe-10', className)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...props}
          type={isPassword && revealed ? 'text' : props.type}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => {
              setRevealed((shown) => !shown);
            }}
            // Off the tab order: it sits between the password fields, and a
            // keyboard user tabbing through the form wants the next field.
            tabIndex={-1}
            aria-pressed={revealed}
            aria-controls={inputId}
            aria-label={revealed ? t('common.hidePassword') : t('common.showPassword')}
            className="absolute inset-y-0 end-0 flex items-center px-3 text-muted transition-colors hover:text-text"
          >
            {revealed ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
          </button>
        )}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
